import { filter, includes, map } from 'underscore';
import { SelectOption } from '@bingads-webui-grid/well-known';
import {
  ColumnConversionsCredit,
  ColumnImpressions,
  ColumnClicks,
  ColumnCTR,
  ColumnSpend,
  ColumnAverageCPC,
  ColumnAdvertiserReportedRevenue,
  ColumnRevenueOnAdSpend,
  ColumnDownloads,
  ColumnFirstLaunches,
  ColumnPurchases,
  ColumnSubscriptions,
  ColumnPostClickDownloadRate,
  ColumnCostPerDownload,
  ColumnPostClickFirstLaunchRate,
  ColumnCostPerFirstLaunch,
  ColumnPostInstallPurchaseRate,
  ColumnCostPerPurchase,
  ColumnPostInstallSubscriptionRate,
  ColumnCostPerSubscription,
  ColumnVideoViews,
  ColumnViewThroughRate,
  ColumnAverageCPV,
  ColumnTotalWatchTimeInMS,
  ColumnAverageWatchTimePerImpression,
  ColumnAverageWatchTimePerVideoView,
  ColumnVideoViewsAt25Percent,
  ColumnVideoViewsAt50Percent,
  ColumnVideoViewsAt75Percent,
  ColumnCompletedVideoViews,
  ColumnVideoCompletionRate,
} from '@bingads-webui-grid/campaignui-common-columns';

import { StatusColumn } from './status';
import { AssetGroupColumn } from './asset-group';
import { DeliveryStatusColumn } from './delivery-status';
import { AssetsColumn } from './assets';
import { AudienceSignalColumn } from './audience-signal';
import { StartDateColumn } from './start-date';
import { EndDateColumn } from './end-date';

export const defaultColumnKeys = [
  'Status',
  'AssetGroup',
  'DeliveryStatus',
  'Assets',
  'AudienceSignal',
  'ConversionsCredit',
  'Spend',
  'Impressions',
  'Clicks',
  'CTR',
  'AverageCPC',
  'Downloads',
  'FirstLaunches',
  'CostPerFirstLaunch',
];

const lockedColumnKeys = [
  'Status',
  'AssetGroup',
];


const attributesColumns = {
  Status: StatusColumn,
  AssetGroup: AssetGroupColumn,
  DeliveryStatus: DeliveryStatusColumn,
  Assets: AssetsColumn,
  AudienceSignal: AudienceSignalColumn,
  StartDate: StartDateColumn,
  EndDate: EndDateColumn,
};

const performanceColumns = {
  Impressions: ColumnImpressions,
  Clicks: ColumnClicks,
  CTR: ColumnCTR,
  Spend: ColumnSpend,
  AverageCPC: ColumnAverageCPC,
  Downloads: ColumnDownloads,
  FirstLaunches: ColumnFirstLaunches,
  Purchases: ColumnPurchases,
  Subscription: ColumnSubscriptions,
  PostClickDownloadRate: ColumnPostClickDownloadRate,
  CostPerDownload: ColumnCostPerDownload,
  PostClickFirstLaunchRate: ColumnPostClickFirstLaunchRate,
  CostPerFirstLaunch: ColumnCostPerFirstLaunch,
  PostInstallPurchaseRate: ColumnPostInstallPurchaseRate,
  CostPerPurchase: ColumnCostPerPurchase,
  PostInstallSubscriptionRate: ColumnPostInstallSubscriptionRate,
  CostPerSubscription: ColumnCostPerSubscription,
};

const conversionsColumns = {
  ConversionsCredit: ColumnConversionsCredit,
  AdvertiserReportedRevenue: ColumnAdvertiserReportedRevenue,
  RevenueOnAdSpend: ColumnRevenueOnAdSpend,
};

const videoMetricsColumns = {
  VideoViews: ColumnVideoViews,
  ViewThroughRate: ColumnViewThroughRate,
  AverageCPV: ColumnAverageCPV,
  TotalWatchTimeInMS: ColumnTotalWatchTimeInMS,
  AverageWatchTimePerImpression: ColumnAverageWatchTimePerImpression,
  AverageWatchTimePerVideoView: ColumnAverageWatchTimePerVideoView,
  VideoViewsAt25Percent: ColumnVideoViewsAt25Percent,
  VideoViewsAt50Percent: ColumnVideoViewsAt50Percent,
  VideoViewsAt75Percent: ColumnVideoViewsAt75Percent,
  CompletedVideoViews: ColumnCompletedVideoViews,
  VideoCompletionRate: ColumnVideoCompletionRate,
};

const getAllColumns = (isUnifiedAppAds) => {
  const allColumns = {
    ...attributesColumns,
    ...performanceColumns,
    ...conversionsColumns,
  };

  if (isUnifiedAppAds) {
    return {
      ...allColumns,
      ...videoMetricsColumns,
    };
  }
  return allColumns;
};


export const buildColumns = ({
  i18n,
  isReadOnlyUser,
  selectedColumnKeys,
  permissions,
  isMobileAppCampaign,
  ...rest
}) => {
  let filteredColumnKeys = selectedColumnKeys;

  const allColumns = getAllColumns(isMobileAppCampaign);

  // Remove columns that not exist anymore, e.g. Conversions has been changed to ConversionsCredit
  filteredColumnKeys = filter(filteredColumnKeys, key => allColumns[key]);

  return map(filteredColumnKeys, key => allColumns[key]({
    i18n, isReadOnlyUser, isMobileAppCampaign, ...rest, permissions,
  }));
};

export function getColumnCategories({ i18n, isMobileAppCampaign }) {
  const filteredAttributesColumnKeys = Object.keys(attributesColumns);

  const attributesCategory = {
    name: i18n.getString(_TL_('Attributes')),
    type: 'Attributes',
    options: filteredAttributesColumnKeys.map(column => ({
      key: column,
      name: attributesColumns[column]({ i18n }).name,
      selectOption: includes(lockedColumnKeys, column) ? SelectOption.locked : SelectOption.optional,
    })),
  };

  let perfColumns = { ...performanceColumns };

  if (isMobileAppCampaign) {
    perfColumns = {
      ...performanceColumns,
      ...videoMetricsColumns,
    };
  }


  const performanceCategory = {
    name: i18n.getString(_TL_('Performance')),
    type: 'Performance',
    options: Object.keys(perfColumns).map(column => ({
      key: column,
      name: perfColumns[column]({ i18n }).title,
      selectOption: SelectOption.optional,
    })),
  };

  const conversionsCategory = {
    name: i18n.getString(_TL_('Conversions')),
    type: 'Conversions',
    options: Object.keys(conversionsColumns).map(column => ({
      key: column,
      name: conversionsColumns[column]({ i18n }).title,
      selectOption: SelectOption.optional,
    })),
  };

  return [
    attributesCategory,
    performanceCategory,
    conversionsCategory,
  ];
}
